/**
 * Shared formatting helpers and building blocks for theme-run views
 * (the Root Cause Themes report tab and the run details page).
 */
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import { ThemeJob, ThemeJobStatus, ThemeJobSummary } from '../../types/ticket';
import { downloadCsv, csvDateStamp } from '../../utils/csvExport';

export const POLL_INTERVAL_MS = 3000;

export const STAGE_LABELS: Record<string, string> = {
  queued: 'Queued',
  starting: 'Starting',
  fetching: 'Fetching tickets',
  fetched: 'Fetched tickets',
  chunked: 'Preparing batches',
  extracting: 'Analyzing tickets',
  consolidating: 'Consolidating themes',
  done: 'Done',
  failed: 'Failed',
  cancelled: 'Cancelled',
};

// In-progress statuses — CANCELLING is still active (the worker hasn't yet
// acknowledged the cancel), so callers keep polling until it settles.
export function isActive(status: ThemeJobStatus): boolean {
  return status === 'PENDING' || status === 'RUNNING' || status === 'CANCELLING';
}

export function progressText(job: ThemeJob): string {
  const stage = job.progress?.stage ?? 'queued';
  const label = STAGE_LABELS[stage] ?? stage;
  const p: Record<string, any> = job.progress ?? {};
  if (stage === 'extracting' && p.chunk && p.chunkCount) {
    return `${label} (batch ${p.chunk} of ${p.chunkCount})`;
  }
  if ((stage === 'fetched' || stage === 'chunked') && p.totalTickets != null) {
    return `${label} — ${p.totalTickets} tickets`;
  }
  return label;
}

export function windowText(params?: ThemeJobSummary['params']): string {
  if (!params?.dateFrom && !params?.dateTo) return '—';
  return `${params?.dateFrom || '—'} to ${params?.dateTo || '—'}`;
}

export function severityLabel(sev: string): string {
  return sev.replace('SEV_', 'SEV ');
}

export function severitiesText(params?: ThemeJobSummary['params']): string {
  return params?.severities?.length ? params.severities.map(severityLabel).join(', ') : 'All';
}

// Absolute human-readable timestamp per the Cloudscape timestamps pattern:
// "July 24, 2026, 04:08 (UTC)". Months are never abbreviated.
export function absoluteTime(iso?: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  const formatted = d.toLocaleString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'UTC',
  });
  return `${formatted} (UTC)`;
}

export function timeAgo(iso?: string): string {
  if (!iso) return '';
  const secs = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (secs < 60) return `${secs}s ago`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  return `${Math.floor(secs / 86400)}d ago`;
}

export function StatusBadge({ status }: { status: ThemeJobStatus }) {
  if (status === 'SUCCEEDED') return <StatusIndicator type="success">Succeeded</StatusIndicator>;
  if (status === 'FAILED') return <StatusIndicator type="error">Failed</StatusIndicator>;
  if (status === 'CANCELLED') return <StatusIndicator type="stopped">Cancelled</StatusIndicator>;
  if (status === 'CANCELLING') return <StatusIndicator type="in-progress">Cancelling</StatusIndicator>;
  return <StatusIndicator type="in-progress">{status === 'PENDING' ? 'Queued' : 'Running'}</StatusIndicator>;
}

export function themesToCsv(job: ThemeJob) {
  const rows = (job.result?.themes || []).map((t) => [
    t.title,
    t.ticketCount,
    t.explanation,
    t.ticketIds.join(';'),
  ]);
  downloadCsv(
    `root-cause-themes-${job.jobId.slice(0, 8)}-${csvDateStamp()}.csv`,
    ['theme', 'ticketCount', 'explanation', 'ticketIds'],
    rows,
  );
}
